/**
 * Gets query parameters from URL
 * @return {{ [key: string]: string | string[] }} parameters in JSON format where key
 *   is equal to the name, and value is the value
 */
export function getQueryParams(): Record<
  string,
  string[] | string | undefined
> {
  // Ensure window.location available
  if (!window.location.search) return {};

  // Base string, remove question mark
  const queryString = window.location.search.replace('?', '');
  const queryObject: Record<string, string[] | string> = {};
  queryString.split('&').forEach((item) => {
    if (item) {
      // Split on the first '=' only. `redirect_uri` carries its own query
      // string, and splitting on every '=' dropped its nested value; a
      // valueless param (`?foo`) gives '' rather than the undefined that threw.
      const eq = item.indexOf('=');
      const key = eq === -1 ? item : item.slice(0, eq);
      let setVal: string[] | string = eq === -1 ? '' : item.slice(eq + 1);
      if (setVal.includes(',')) {
        setVal = setVal.split(',').map((v) => decodeURIComponent(v));
      } else {
        setVal = decodeURIComponent(setVal);
      }
      queryObject[key] = setVal;
    }
  });
  return queryObject;
}

/**
 * Creates query parameters from url and object
 * @param  {string} url, base url
 * @param  {object} params, object of key value params to append
 */
export function createQueryParams(
  url = '',
  params: Record<string, string> = {}
) {
  let newUrl = '';

  if (typeof newUrl === 'string') {
    newUrl += url;
  }

  if (typeof params === 'object' && Object.keys(params).length > 0) {
    newUrl += '?';
  }

  Object.keys(params).forEach((key, index) => {
    newUrl += `${key}=${encodeURIComponent(params[key])}`;
    if (index < Object.keys(params).length - 1) {
      newUrl += '&';
    }
  });

  return newUrl;
}

/**
 * @param  {Location} location
 * @return {string[]} contents of '/' exploded url
 */
export function getLocationNameArray(location: Location): string[] {
  if (typeof location !== 'undefined' && typeof location.href !== 'undefined') {
    const locationNames = location.href.split('/').splice(3);
    if (locationNames[locationNames.length - 1] === '') {
      locationNames.pop();
    }
    return locationNames;
  }
  return [];
}

export function constructUrl(
  baseUrl: URL,
  route: string,
  args?: Record<string, any>
): URL {
  const url = new URL(
    [baseUrl.pathname, route].join('/').replace(/\/+/, '/'),
    baseUrl
  );
  if (args) {
    for (const key in args) {
      url.searchParams.append(key, args[key]);
    }
  }
  return url;
}
